import {
  ComponentElementsGenericComponent,
  ComponentElementsGlobalComponent,
  FaultArticle,
} from '@challenger/care/modules/graphql/generated/graphql-types';
import { ApolloQueryResult } from '@apollo/client';
import { SeoService } from '@challenger/care/services/seo.service';
import { Observable, catchError, map, mergeMap, of, throwError } from 'rxjs';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ISEOLandingPageCollection } from '../../common/interfaces/seo-landing-page-collection.interface';
import { SEOStrapiService } from '../../common/services/seo-strapi.service';
import { environment } from '../../../environments/environment';

export enum ERROR {
  PAGE_NOT_FOUND = 'page_not_found',
}

export class FlowResult {
  config: any;
  UIDList: string[];
  articles: any;
}

@Injectable({
  providedIn: 'root',
})
export class SEOFlowService {
  constructor(
    private seoStrapiService: SEOStrapiService,
    @Inject(PLATFORM_ID) private platformId: object,
    private seoService: SeoService
  ) {}

  public getWorkflowArticles(strapiPrefix: string, strapiUID: string): Observable<any> {
    let wfArticles = null;
    if ((isPlatformBrowser(this.platformId) && environment.envName !== 'dev') || environment.envName !== 'review') {
      wfArticles = this.getSessionFlowResult(strapiUID);
    }
    if (wfArticles) {
      return of(wfArticles);
    } else {
      return this.seoStrapiService.getContent(strapiPrefix, strapiUID).pipe(
        map((wfResult: ApolloQueryResult<ISEOLandingPageCollection>) => {
          const seoLandingPages = wfResult?.data?.seoLandingPages;
          if (seoLandingPages === undefined || seoLandingPages?.length < 1) {
            throw new Error(ERROR.PAGE_NOT_FOUND);
          }
          this.seoService.updatePageTitle(seoLandingPages[0]?.titleOfTheBrowser);
          this.seoService.updateMetaTags(seoLandingPages[0]?.MetaTagSection);
          const flowResult = new FlowResult();
          flowResult.config = this.cleanObject(seoLandingPages[0], []);
          flowResult.UIDList = this.getWorkflowUIDList(flowResult.config);
          return flowResult;
        }),
        mergeMap((flowResult) =>
          this.seoStrapiService.getContent(strapiPrefix, flowResult.UIDList).pipe(
            map((wfArticlesResult: ApolloQueryResult<ISEOLandingPageCollection>) => {
              const seoLandingPages = wfArticlesResult?.data?.seoLandingPages;
              flowResult.articles = seoLandingPages;
              return flowResult;
            })
          )
        ),
        catchError((error) => {
          return throwError(() => error);
        })
      );
    }
  }

  formatWorkflowArticles(flowResult: FlowResult, strapiPrefix: string, strapiUID: string) {
    const srcArticles = this.cleanObject(flowResult.articles, []);
    const wfArticles = new Array<FaultArticle>();
    const wfConfig = flowResult.config;

    wfConfig.elements.forEach((global: ComponentElementsGlobalComponent) => {
      const isAdvice = this.getIsAdvice(global.genericComponent[0]);
      let uid = this.getGenericUID(global.genericComponent[0]);

      const wfArticle: FaultArticle = JSON.parse(
        JSON.stringify(
          srcArticles.find((art) => {
            return art.uid === uid;
          })
        )
      );

      global?.genericComponent.forEach((generic: ComponentElementsGenericComponent, genericIndex) => {
        uid = this.getGenericUID(generic);

        if (isAdvice) {
          const slideNum = this.getGenericSlideNum(generic, isAdvice);
          if (genericIndex === 0) {
            (wfArticle.elements[0] as ComponentElementsGlobalComponent).genericComponent =
              new Array<ComponentElementsGenericComponent>();
          }
          const srcArticle = srcArticles.find((art) => {
            return art.uid === uid;
          });
          (wfArticle.elements[0] as ComponentElementsGlobalComponent).genericComponent.push(
            this.mergeObjects(
              (srcArticle.elements[0] as ComponentElementsGlobalComponent).genericComponent[slideNum],
              generic
            )
          );
        } else {
          (wfArticle.elements[0] as ComponentElementsGlobalComponent).genericComponent[0] = this.mergeObjects(
            (wfArticle.elements[0] as ComponentElementsGlobalComponent).genericComponent[0],
            generic
          );
        }
      });

      wfArticles.push(JSON.parse(JSON.stringify(wfArticle)));
    });
    if (isPlatformBrowser(this.platformId)) {
      this.setSessionFlowResult(strapiUID, flowResult);
    }
    return wfArticles;
  }

  public getWorkflowUIDList(wfArticle: FaultArticle) {
    const wfUIDList = new Array<string>();
    wfArticle.elements.forEach((global: ComponentElementsGlobalComponent) => {
      global.genericComponent.forEach((generic: ComponentElementsGenericComponent) => {
        const uid = generic?.uid?.split('.')[0];
        if (!wfUIDList.includes(uid)) {
          wfUIDList.push(uid);
        }
      });
    });
    return wfUIDList;
  }

  public getIsAdvice(generic: ComponentElementsGenericComponent): boolean {
    return generic?.uid?.includes('.');
  }

  public getGenericUID(generic: ComponentElementsGenericComponent): string {
    return generic?.uid.split('.')[0];
  }

  public getGenericSlideNum(generic: ComponentElementsGenericComponent, isAdvice: boolean): number {
    if (isAdvice) {
      return parseInt(generic?.uid.split('.')[1], 10) - 1;
    }
    return 0;
  }

  public cleanObject(obj: any, removeKeys: Array<string>): any {
    // If the input is not an object or is null, return it as is
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    // If the input is an array, filter out null elements and recursively clean each element
    if (Array.isArray(obj)) {
      return obj
        .filter((el) => el !== null)
        .map((el) => this.cleanObject(el, removeKeys))
        .filter((el) => !(typeof el === 'object' && Object.keys(el).length === 0));
    }

    // If the input is an object, create a new object with only non-null properties and recursively clean each property
    const cleanedObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (removeKeys.includes(key.toLocaleLowerCase())) {
        continue;
      } else if (value === null || value?.toString()?.length === 0) {
        continue;
      } else if (typeof value === 'object') {
        const cleanedValue = this.cleanObject(value, removeKeys);
        if (Array.isArray(cleanedValue) && cleanedValue.length === 0) {
          continue;
        } else if (typeof cleanedValue === 'object' && Object.keys(cleanedValue).length === 0) {
          continue;
        }
        cleanedObj[key] = cleanedValue;
      } else {
        cleanedObj[key] = value;
      }
    }
    return cleanedObj;
  }

  public mergeObjects(obj1: any, obj2: any): any {
    const merged: any = {};
    for (const key of Object.keys(obj1)) {
      if (key in obj2 && obj2[key]) {
        if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object' && !Array.isArray(obj1[key])) {
          merged[key] = this.mergeObjects(obj1[key], obj2[key]);
        } else if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
          merged[key] = this.mergeArraysOfObjects(obj1[key], obj2[key]);
        } else if (obj2[key] !== '~') {
          merged[key] = obj2[key];
        }
      } else {
        merged[key] = obj1[key];
      }
    }

    for (const key of Object.keys(obj2)) {
      if (!(key in obj1) && obj2[key] !== null) {
        merged[key] = obj2[key];
      }
    }

    return merged;
  }

  public mergeArraysOfObjects(arr1: any[], arr2: any[]): any[] {
    const merged: any[] = [...arr1];

    for (let i = 0; i < arr2.length; i++) {
      const match = merged[i];

      if (match) {
        merged[i] = this.mergeObjects(match, arr2[i]);
      } else {
        merged.push(arr2[i]);
      }
    }

    return merged;
  }
  setSessionFlowResult(wfUID: string, flowResult: FlowResult) {
    sessionStorage.removeItem(`${wfUID}.articles`);
    sessionStorage.setItem(`${wfUID}.config`, JSON.stringify(flowResult));
  }

  getSessionFlowResult(wfUID: string): FlowResult {
    return JSON.parse(sessionStorage.getItem(`${wfUID}.config`));
  }
}
